import { useState, useCallback, useMemo } from 'react';

const DISTRIBUTOR_TOKEN_KEY = 'distributor_token';
const DISTRIBUTOR_USER_KEY = 'distributor_user';
const MANAGE_URL = 'https://manage.realtimefeedback.com';

export type DistributorUser = {
  first_name: string;
  last_name: string | null;
  email: string;
  phone_number: string | null;
  company: string | null;
  additional_info: string | null;
};

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

function getStoredItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStoredItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Authentication remains available to this hook instance in memory.
  }
}

function removeStoredItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage may be blocked by browser privacy or sandbox settings.
  }
}

export function getDistributorToken(): string | null {
  const token = getStoredItem(DISTRIBUTOR_TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    clearDistributorAuth();
    return null;
  }
  return token;
}

export function clearDistributorAuth() {
  removeStoredItem(DISTRIBUTOR_TOKEN_KEY);
  removeStoredItem(DISTRIBUTOR_USER_KEY);
}

export function redirectToManage() {
  // Distributors typically arrive via a per-business deep link
  // (manage.*/business/<id>/sso-redirect?…) embedded in their email. When their
  // token expires here, we don't know their business_id, so just send them home
  // to manage.* and let them re-click the link.
  clearDistributorAuth();
  window.location.href = MANAGE_URL;
}

export function useDistributorAuth() {
  const [token, setTokenState] = useState<string | null>(() =>
    getDistributorToken(),
  );
  const [user, setUserState] = useState<DistributorUser | null>(() => {
    const stored = getStoredItem(DISTRIBUTOR_USER_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as DistributorUser;
    } catch {
      return null;
    }
  });

  const isAuthenticated = useMemo(() => {
    if (!token) return false;
    return !isTokenExpired(token);
  }, [token]);

  const login = useCallback((jwt: string, distributorUser: DistributorUser) => {
    setTokenState(jwt);
    setUserState(distributorUser);
    setStoredItem(DISTRIBUTOR_TOKEN_KEY, jwt);
    setStoredItem(DISTRIBUTOR_USER_KEY, JSON.stringify(distributorUser));
  }, []);

  const logout = useCallback(() => {
    redirectToManage();
  }, []);

  return { token, user, isAuthenticated, login, logout };
}
